import { FinalTournamentView } from '../components/scoring/FinalTournamentView';
import { mergeScoringLiveBoard, useScoringLiveBoard } from '../hooks/useScoringLiveBoard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { scoringTitle } from '../lib/page-title';
import { Link, useNavigate, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { Layout } from '../components/layout/Layout';
import { ApiError, apiFetch } from '../lib/api';
import { useScoringTournament, type LeaderboardRow, type ShooterRow, type TournamentDetail } from '../hooks/useScoringTournament';
import {
  rememberListScroll,
  scoringCheckInCallerPath,
  scoringCheckInPath,
  scoringListPath,
  scoringRound1TeamsPath,
  scoringShooterPath,
  takeListScroll,
  type ScoringFromListState,
} from '../lib/scoring-routes';
import { filterShootersByQuery, readEventDayTab } from '../lib/event-day';
import {
  RIFLE_TOURNAMENT_SLUG,
  SHOOTER_GENDERS,
  SHOOTER_GENDER_LABELS,
  bibPhoneMessage,
  isParseError,
  normalizeBibPrefix,
  parseBib,
  type ShooterGender,
} from '../lib/shooting';
import { useCheckInPoll } from '../hooks/useCheckInPoll';
import { useEventDayToggle } from '../hooks/useEventDayToggle';
import { EventDayBar, EventDayMore } from '../components/scoring/EventDayBar';
import { LaneScoreEntry } from '../components/scoring/LaneScoreEntry';
import { filterByGender, parseGenderFilter, type GenderFilter } from '../lib/scoreboard-view';
import { round1TeamsEnabled, wholeNumberScoring } from '../lib/round1';
import { resultsCsv, resultsFileName } from '../lib/results-csv';
import { FormatCard } from '../components/scoring/FormatCard';
import { PublishCard } from '../components/scoring/PublishCard';
import { DisplayPageCard } from '../components/scoring/DisplayPageCard';
import { CreateTournamentCard } from '../components/scoring/CreateTournamentCard';
import { GenderRosterFilter } from '../components/scoring/GenderRosterFilter';
import { addedToast, pushScoringToast } from '../components/common/admin-toasts';
import { bigScreenPath } from '../lib/big-screen';
import { tournamentHasRound2 } from '../lib/round2';
import { Round2SetupCard } from '../components/scoring/Round2SetupCard';

export const scoringFieldClass =
  'w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-3 text-[var(--color-text-primary)]';

const fromList: ScoringFromListState = { fromList: true };

/** /admin/scoring and /admin/scoring/:slug — find a shooter, then open their own score page. */
export function ScoringAdminPage() {
  const { isAdmin: admin, isOrganizer } = useAuth();
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const genderFilter = parseGenderFilter(searchParams.get('gender'));
  const { tournaments, putTournament, slug, detail, notFound, error, setDetail } = useScoringTournament(slugParam);
  const finished = detail?.tournament.status === 'final';
  const isAdmin = admin || Boolean(isOrganizer && slug === RIFLE_TOURNAMENT_SLUG);
  const [creating, setCreating] = useState(false);
  const [createdNote, setCreatedNote] = useState<string | null>(null);
  const bibPrefix = normalizeBibPrefix(detail?.bibPrefix);
  const eventDayHome = slug === RIFLE_TOURNAMENT_SLUG || Boolean(detail && tournamentHasRound2(detail.tournament));
  const { eventDay, busy: eventDayBusy, error: eventDayError, toggle: toggleEventDay } = useEventDayToggle(detail, setDetail);
  useScoringLiveBoard(slug, Boolean(isAdmin && detail), board => setDetail(current => mergeScoringLiveBoard(current, board)));
  const checkIn = useCheckInPoll(detail?.tournament.id ?? null, Boolean(isAdmin && eventDayHome));
  const scoresEntered = (detail?.leaderboard.rows ?? []).filter((row) => (row.shotsFired ?? 0) > 0).length;
  const rosterTotal = detail?.shooters.length ?? 0;

  const [newName, setNewName] = useState('');
  const [newGender, setNewGender] = useState<ShooterGender | ''>('');
  const [newBib, setNewBib] = useState(detail?.nextRfl ?? '');
  /** True once the admin edits the RFL box, so a refreshed suggestion does not overwrite it. */
  const [bibEdited, setBibEdited] = useState(false);
  const bibCheck = parseBib(newBib, bibPrefix);
  const bibError = isParseError(bibCheck) ? bibCheck.error : null;
  /** Shown while typing: only the phone-number case, so a half-typed number is not flagged. */
  const liveBibError = bibError === bibPhoneMessage(bibPrefix) ? bibError : null;
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const restored = useRef(false);

  // Back from a shooter page: put the list where it was once the rows are on screen.
  useEffect(() => {
    if (restored.current || !detail) return;
    restored.current = true;
    const y = takeListScroll(slug);
    if (navigationType === 'POP' && y !== null) {
      window.requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [detail, slug, navigationType]);

  useEffect(() => {
    document.title = scoringTitle(detail?.tournament.title);
  }, [detail?.tournament.title]);

  // Each phone reopens on the job it used last. A search link stays on Scores.
  useEffect(() => {
    if (!isAdmin || !eventDayHome || !detail || finished) return;
    if (searchParams.get('q') || searchParams.get('gender')) return;
    const tab = readEventDayTab(slug);
    if (tab === 'check-in') navigate(scoringCheckInPath(slug), { replace: true });
    else if (tab === 'caller') navigate(scoringCheckInCallerPath(slug), { replace: true });
  }, [isAdmin, eventDayHome, detail?.tournament.status, finished, slug, navigate, searchParams]);

  /** Leaderboard rows in the chosen group, ranked within it (All = the overall ranks). */
  const groupRows = useMemo(() => filterByGender(detail?.leaderboard.rows ?? [], genderFilter), [detail, genderFilter]);

  const totals = useMemo(() => {
    const map = new Map<string, LeaderboardRow>();
    for (const row of groupRows) map.set(row.shooterId, row);
    return map;
  }, [groupRows]);

  const genderCounts = useMemo(() => {
    const counts: Record<GenderFilter, number> = { all: 0, male: 0, female: 0 };
    for (const shooter of detail?.shooters ?? []) {
      counts.all += 1;
      if (shooter.gender) counts[shooter.gender] += 1;
    }
    return counts;
  }, [detail]);

  const shooters = useMemo(() => {
    const roster = detail?.shooters ?? [];
    const named = (query.trim() ? filterShootersByQuery(roster, query, bibPrefix) : roster).filter(
      (shooter) => genderFilter === 'all' || shooter.gender === genderFilter,
    );
    return named;
  }, [detail, query, genderFilter, bibPrefix]);

  function setQuery(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }

  function setGenderFilter(value: GenderFilter) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('gender');
    else next.set('gender', value);
    setSearchParams(next, { replace: true });
  }

  const groupQuery = null;
  const genderQuery = genderFilter === 'all' ? null : genderFilter;

  async function downloadResults() {
    if (!detail) return;
    try {
    const fresh = await apiFetch<TournamentDetail>(`/api/admin/shooting/tournaments/${detail.tournament.id}`);
    const exportRows = filterByGender(fresh.leaderboard.rows, genderFilter);
    const csv = resultsCsv(exportRows, fresh.tournament.shotsPlanned ?? 15,
      genderFilter === 'all' ? fresh.leaderboard.round1?.teams.map((team) => ({ name: team.name, totalDisplay: team.totalDisplay })) : undefined);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = resultsFileName(detail.tournament.slug, genderFilter);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { pushScoringToast(err instanceof ApiError ? err.message : 'Could not download the results.'); }
  }

  const nextRfl = detail?.nextRfl;
  useEffect(() => {
    if (!bibEdited && nextRfl) setNewBib(nextRfl);
  }, [nextRfl, bibEdited]);

  async function addShooter(event: React.FormEvent) {
    event.preventDefault();
    if (!detail) return;
    if (bibError) {
      setFormError(bibError);
      return;
    }
    setAdding(true);
    setFormError(null);
    try {
      const body = await apiFetch<{ shooter: ShooterRow; leaderboard: TournamentDetail['leaderboard']; nextRfl?: string }>(
        `/api/admin/shooting/tournaments/${detail.tournament.id}/shooters`,
        {
          method: 'POST',
          body: JSON.stringify({ name: newName, bib: bibCheck.ok ? bibCheck.bib : newBib, gender: newGender || null }),
        },
      );
      setDetail((current) => ({
        ...current,
        shooters: [...current.shooters, body.shooter],
        leaderboard: body.leaderboard,
        nextRfl: body.nextRfl,
      }));
      setNewName('');
      setNewGender('');
      setNewBib(body.nextRfl ?? '');
      setBibEdited(false);
      pushScoringToast(addedToast(body.shooter.name));
      rememberListScroll(slug, window.scrollY);
      navigate(scoringShooterPath(slug, body.shooter, query, groupQuery, genderQuery), { state: { ...fromList, added: true } });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not add that shooter.');
    } finally {
      setAdding(false);
    }
  }

  return (
    <Layout>
      <div className="mx-auto max-w-3xl space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm text-[var(--color-text-tertiary)]">
              <Link to="/admin" className="underline-offset-2 hover:underline">
                Admin
              </Link>
            </p>
            <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
              {detail?.tournament.title ?? 'Rifle scoring'}
            </h1>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {finished ? 'Manage visibility and event operations. Final scores, teams and format stay locked.' : eventDayHome
                ? 'Check in, call the lanes, or enter scores. Each phone stays on its own tab.'
                : 'Tap a shooter to enter their shots. Each shot saves on its own and the public board updates shot by shot.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
          {isAdmin && detail && tournamentHasRound2(detail.tournament) && !eventDayHome ? (
            <Link
              to={scoringCheckInPath(slug)}
              className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-medium text-white"
            >
              Check in
            </Link>
          ) : null}
          <a
              href={`/scoreboard/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4 text-sm font-medium text-[var(--color-text-primary)]"
            >
              Open the live board
            </a>
            {admin && !creating ? (
              <button
                type="button"
                onClick={() => {
                  setCreating(true);
                  setCreatedNote(null);
                }}
                className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-medium text-white"
              >
                New tournament
              </button>
            ) : null}
          </div>
        </header>

        {error ? (
          <p className="rounded-[var(--radius-md)] bg-[var(--color-error-subtle)] px-4 py-3 text-sm text-[var(--color-error-text)]">
            {error}
          </p>
        ) : null}

        {tournaments && tournaments.length === 0 ? (
          <p className="text-sm text-[var(--color-text-secondary)]">
            Scoring is not set up yet. The database update that adds the rifle tournament still needs to run.
          </p>
        ) : null}

        {tournaments && tournaments.length > 0 && notFound ? (
          <p className="text-sm text-[var(--color-text-secondary)]">
            There is no tournament called “{slug}”.{' '}
            <Link to="/admin/scoring" className="font-medium text-[var(--color-accent)] underline-offset-2 hover:underline">
              Open rifle scoring
            </Link>
          </p>
        ) : null}

        {createdNote ? (
          <p className="rounded-[var(--radius-md)] bg-[var(--color-surface-2)] px-4 py-3 text-sm text-[var(--color-text-primary)]" role="status">
            {createdNote}
          </p>
        ) : null}

        {admin && creating ? (
          <CreateTournamentCard
            onCancel={() => setCreating(false)}
            onCreated={({ tournament, imported }) => {
              putTournament(tournament);
              setCreating(false);
              setCreatedNote(`Created “${tournament.title}” with ${imported} shooter${imported === 1 ? '' : 's'} imported.`);
              navigate(scoringListPath(tournament.slug));
            }}
          />
        ) : null}


        {tournaments && tournaments.length > 1 ? (
          <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
            Tournament
            <select
              className={`${scoringFieldClass} mt-1 h-12`}
              value={slug}
              onChange={(event) => navigate(scoringListPath(event.target.value))}
            >
              {tournaments.map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {finished && detail ? (
          <>
            {isAdmin ? (
              <section aria-label="Finished tournament management" className="space-y-4">
                <h2 className="text-lg font-semibold">Tournament management</h2>
                <PublishCard key={`publish-${detail.tournament.id}`} tournament={detail.tournament} onSaved={(tournament) => {
                  putTournament(tournament);
                  setDetail((current) => ({ ...current, tournament: { ...current.tournament, ...tournament } }));
                }} />
                <DisplayPageCard key={`display-${detail.tournament.id}`} tournamentId={detail.tournament.id} slug={slug} leaderboard={detail.leaderboard} onSaved={(leaderboard) => setDetail((current) => ({ ...current, leaderboard }))} />
                {tournamentHasRound2(detail.tournament) ? <Round2SetupCard key={detail.tournament.id} tournamentId={detail.tournament.id} slug={slug} finished /> : null}
                <nav aria-label="Tournament administration" className="flex flex-wrap gap-4 text-[var(--color-accent)]">
                  <a href={bigScreenPath(slug)} target="_blank" rel="noreferrer">Display theme and round</a>
                  <Link to="/admin">Manage events</Link>
                </nav>
                <EventDayBar slug={slug} place="scores" isAdmin={isAdmin} eventDay={eventDay} toggleBusy={eventDayBusy} onToggle={toggleEventDay} toggleError={eventDayError} counts={{
                  checkedIn: checkIn ? checkIn.shooters.filter((shooter) => shooter.status !== 'not_checked_in').length : null,
                  total: rosterTotal,
                  shootingNow: checkIn ? checkIn.lanes.filter((lane) => lane.shooterId).length : null,
                  scoresEntered,
                  waiting: checkIn ? checkIn.waiting.length : null,
                }} />
              </section>
            ) : null}
            <FinalTournamentView detail={detail} embedded />
          </>
        ) : <>
        {eventDayHome && detail ? (
          <EventDayBar
            slug={slug}
            place="scores"
            isAdmin={isAdmin}
            eventDay={eventDay}
            toggleBusy={eventDayBusy}
            onToggle={toggleEventDay}
            toggleError={eventDayError}
            counts={{
              checkedIn: checkIn ? checkIn.shooters.filter((shooter) => shooter.status !== 'not_checked_in').length : null,
              total: rosterTotal,
              shootingNow: checkIn ? checkIn.lanes.filter((lane) => lane.shooterId).length : null,
              scoresEntered,
              waiting: checkIn ? checkIn.waiting.length : null,
            }}
          />
        ) : null}

        {eventDay && detail ? (
          <EventDayMore enabled>
            {isAdmin && tournamentHasRound2(detail.tournament) ? (
              <Round2SetupCard tournamentId={detail.tournament.id} slug={slug} />
            ) : null}
            {isAdmin ? (
              <FormatCard
                key={detail.tournament.id}
                tournament={detail.tournament}
                onSaved={(result) => {
                  putTournament(result.tournament);
                  setDetail((current) => ({ ...current, tournament: { ...current.tournament, ...result.tournament }, leaderboard: result.leaderboard }));
                }}
              />
            ) : null}
            <button
              type="button"
              onClick={downloadResults}
              className="inline-flex min-h-12 items-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4 text-sm font-medium text-[var(--color-text-primary)]"
            >
              Download results (CSV)
            </button>
          </EventDayMore>
        ) : null}

        {eventDayHome && detail ? (
          <LaneScoreEntry
            scorer={checkIn?.scorer}
            onScoresChanged={() => checkIn?.refresh?.()}
            teams={detail.leaderboard.round1?.teams}
            tournamentId={detail.tournament.id}
            bibPrefix={bibPrefix}
            shotsPerShooter={detail.tournament.shotsPlanned ?? 15}
            countedShots={detail.tournament.shotsCounted ?? 10}
            lanes={checkIn?.lanes ?? null}
            lanesReady={!isAdmin || checkIn != null}
            showLanes={isAdmin}
            shooters={detail.shooters}
            scores={detail.scores}
            leaderboardRows={detail.leaderboard.rows}
            whole={wholeNumberScoring(detail.tournament)}
            onSaved={({ leaderboard, scores }) => setDetail((current) => ({
              ...current,
              ...(leaderboard && (!current.leaderboard.updatedAt || (leaderboard.updatedAt && leaderboard.updatedAt >= current.leaderboard.updatedAt)) ? { leaderboard } : {}),
              scores,
            }))}
          />
        ) : null}

        {isAdmin && detail ? (
          <PublishCard
            key={`publish-${detail.tournament.id}`}
            tournament={detail.tournament}
            onSaved={(tournament) => {
              putTournament(tournament);
              setDetail((current) => ({ ...current, tournament: { ...current.tournament, ...tournament } }));
            }}
          />
        ) : null}

        {detail ? (
          <DisplayPageCard
            tournamentId={detail.tournament.id}
            slug={detail.tournament.slug}
            leaderboard={detail.leaderboard}
            onSaved={(leaderboard) => setDetail((current) => ({ ...current, leaderboard }))}
          />
        ) : null}

        {!eventDay && isAdmin && detail && tournamentHasRound2(detail.tournament) ? (
          <Round2SetupCard tournamentId={detail.tournament.id} slug={slug} />
        ) : null}

        {!eventDay && isAdmin && detail ? (
          <FormatCard
            key={detail.tournament.id}
            tournament={detail.tournament}
            onSaved={(result) => {
              putTournament(result.tournament);
              setDetail((current) => ({ ...current, tournament: { ...current.tournament, ...result.tournament }, leaderboard: result.leaderboard }));
            }}
          />
        ) : null}

        <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
          Find a shooter
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Name or ${bibPrefix} number`}
            className={`${scoringFieldClass} mt-1 h-12 text-base`}
            autoComplete="off"
          />
        </label>

        {detail && !isAdmin ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1" role="group" aria-label="Show shooters">
              {(['all', ...SHOOTER_GENDERS] as GenderFilter[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={genderFilter === value}
                  onClick={() => setGenderFilter(value)}
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-sm font-medium ${
                    genderFilter === value
                      ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]'
                  }`}
                >
                  {value === 'all' ? 'All' : SHOOTER_GENDER_LABELS[value]}
                  <span className="tabular-nums opacity-70">{genderCounts[value]}</span>
                </button>
              ))}
            </div>
            {eventDay ? null : (
              <button
                type="button"
                onClick={downloadResults}
                className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4 text-sm font-medium text-[var(--color-text-primary)]"
              >
                Download results (CSV)
              </button>
            )}
          </div>
        ) : null}

        {detail && isAdmin ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <GenderRosterFilter value={genderFilter} counts={genderCounts} onChange={setGenderFilter} />
              {round1TeamsEnabled(detail.tournament) ? (
                <Link to={scoringRound1TeamsPath(slug)} className="inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium text-[var(--color-accent)]">
                  Teams
                </Link>
              ) : null}
            </div>
            {eventDay ? null : (
              <button
                type="button"
                onClick={downloadResults}
                className="inline-flex min-h-11 items-center rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4 text-sm font-medium text-[var(--color-text-primary)]"
              >
                Download results (CSV)
              </button>
            )}
          </div>
        ) : null}

        <ul className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)]">
          {shooters.map((shooter) => {
            const standing = totals.get(shooter.id);
            return (
              <li key={shooter.id} className="border-b border-[var(--color-border)] last:border-b-0">
                <Link
                  to={scoringShooterPath(slug, shooter, query, groupQuery, genderQuery)}
                  state={fromList}
                  onClick={() => rememberListScroll(slug, window.scrollY)}
                  className="flex min-h-14 w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--color-surface-2)]"
                >
                  <span className="min-w-0">
                    <span className="block font-medium text-[var(--color-text-primary)]">{shooter.name}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--color-text-tertiary)]">
                      <span>
                        {shooter.bib ?? 'No number'}
                        {shooter.gender ? ` · ${SHOOTER_GENDER_LABELS[shooter.gender]}` : ''}
                        {shooter.teamName ? ` · ${shooter.teamName}` : ''}
                      </span>
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="block text-lg font-semibold tabular-nums text-[var(--color-text-primary)]">
                      {standing && standing.rank > 0 ? standing.totalDisplay : '–'}
                    </span>
                    <span className="text-xs text-[var(--color-text-tertiary)]">
                      {standing && standing.rank > 0
                        ? `${genderFilter === 'all' ? 'Rank' : `${SHOOTER_GENDER_LABELS[genderFilter]} rank`} ${standing.rank}${standing.shotsPlanned ? ` · ${standing.shotsFired ?? 0}/${standing.shotsPlanned} shots` : ''}`
                        : 'No score yet'}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
          {!detail && !error && !notFound && tournaments?.length !== 0 ? (
            <li className="px-4 py-6 text-sm text-[var(--color-text-secondary)]">Loading shooters…</li>
          ) : null}
          {detail && shooters.length === 0 ? (
            <li className="px-4 py-6 text-sm text-[var(--color-text-secondary)]">
              {genderFilter !== 'all' && genderCounts[genderFilter] === 0
                ? `Nobody is tagged ${SHOOTER_GENDER_LABELS[genderFilter]} yet. Set it on each shooter's page.`
                  : 'No shooter matches that search.'}
            </li>
          ) : null}
        </ul>

        {detail ? (
          <form
            onSubmit={(event) => void addShooter(event)}
            className="grid gap-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] p-3 sm:grid-cols-[1fr_9rem_8rem_auto] sm:items-start"
          >
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">Missing name</span>
              <input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Add a name that is missing"
                className={`${scoringFieldClass} h-12`}
                aria-label="New shooter name"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">{bibPrefix} number</span>
              <input
                value={newBib}
                onChange={(event) => {
                  setNewBib(event.target.value);
                  setBibEdited(true);
                  setFormError(null);
                }}
                placeholder={`e.g. ${bibPrefix}-0091`}
                autoComplete="off"
                autoCapitalize="characters"
                className={`${scoringFieldClass} h-12 w-full ${liveBibError ? 'border-[var(--color-error)]' : ''}`}
                aria-label={`${bibPrefix} number (optional)`}
                aria-invalid={Boolean(liveBibError || (formError && formError === bibError))}
                aria-describedby="new-shooter-rfl-hint"
              />
              {liveBibError ? (
                <span id="new-shooter-rfl-hint" role="alert" className="mt-1 block text-sm text-[var(--color-error-text)]">
                  {liveBibError}
                </span>
              ) : (
                <span id="new-shooter-rfl-hint" className="mt-1 block text-xs text-[var(--color-text-tertiary)]">
                  Not a phone number. Pre-filled with the next free one.
                </span>
              )}
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">Men / Women</span>
              <select
                value={newGender}
                onChange={(event) => setNewGender(event.target.value as ShooterGender | '')}
                className={`${scoringFieldClass} h-12`}
                aria-label="Men or Women (optional)"
              >
                <option value="">Not set</option>
                {SHOOTER_GENDERS.map((value) => (
                  <option key={value} value={value}>
                    {SHOOTER_GENDER_LABELS[value]}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={adding || !newName.trim() || Boolean(liveBibError)}
              className="h-12 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] px-4 text-sm font-medium disabled:opacity-50 sm:mt-5"
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
            {formError && formError !== liveBibError ? (
              <p className="text-sm text-[var(--color-error-text)] sm:col-span-4" role="alert">
                {formError}
              </p>
            ) : null}
          </form>
        ) : null}
        </>}
      </div>
    </Layout>
  );
}

